import { Button, Checkbox, TableCell, TableRow } from '@folio/components';
import { cn } from '@folio/lib';
import type { PolicyBranchesForBrokerEntity, PolicyForBrokerEntity } from 'korint-sdk';
import type { PolicyColumnConfig } from 'korint-sdk/react';
import _ from 'lodash';
import { ChevronRight, EyeIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BooleanCell } from './BooleanCell';
import { DateCell } from './DateCell';
import { PriceCell } from './PriceCell';
import { StringCell } from './StringCell';
import { ProductCell } from './ProductCell';
import { TierCell } from './TierCell';
import { PolicyStatus } from '../../features/PolicyStatus';
import { Badge } from '@folio/components/ui/badge';
import { FRAUD_STATUS_TONE } from '../../features/document-viewer/fraudStatus';

type PolicyRowProps = Pick<PolicyForBrokerEntity, 'branches'> & {
  columns: ReadonlyArray<PolicyColumnConfig>;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  policyLinkSuffix?: string;
};

const getColumnValue = (
  column: PolicyColumnConfig,
  data: PolicyBranchesForBrokerEntity,
): string | number | boolean | Date | undefined | null => {
  if ('path' in column) {
    return _.get(data, column.path);
  }

  if ('customField' in column) {
    const entity =
      column.customField === 'customers' ? data.customers?.[0] : data[column.customField];
    const value = entity?.customFields?.find(field => field.key === column.name)?.value;
    return Array.isArray(value) ? undefined : value;
  }

  if ('accessor' in column) {
    return column.accessor(data);
  }
};

export const PolicyRow = ({
  branches,
  columns,
  selectable,
  selected,
  onToggleSelect,
  policyLinkSuffix = '',
}: PolicyRowProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return branches.map((data, index, branches) => {
    const { policy, branch } = data;

    const isMultipleRow = branches.length > 1;
    const elementIsNotLast = index < branches.length - 1;

    if (isMultipleRow && index > 0 && !isOpen) {
      return null;
    }

    return (
      <TableRow
        key={policy.id + (branch?.branchId ?? 'BASE')}
        className={cn({
          'border-b-0': isMultipleRow && elementIsNotLast && isOpen,
          'bg-gray-100 hover:bg-gray-100': isMultipleRow && isOpen && !selected,
          'cursor-pointer': isMultipleRow && index === 0,
          'hover:bg-gray-200': isMultipleRow && index === 0 && isOpen && !selected,
          'bg-primary/5': selected,
        })}
        onClick={isMultipleRow && index === 0 ? () => setIsOpen(o => !o) : undefined}
      >
        {selectable && (
          <TableCell className="w-10">
            {index === 0 && (
              <Checkbox
                checked={selected}
                onCheckedChange={() => onToggleSelect?.()}
                onClick={e => e.stopPropagation()}
              />
            )}
          </TableCell>
        )}
        <TableCell>
          {isMultipleRow && index === 0 && (
            <ChevronRight
              className={cn('w-5 h-5 transition-transform duration-200', {
                'rotate-90': isOpen,
              })}
            />
          )}
        </TableCell>
        {columns.map(column => {
          const value = getColumnValue(column, data);

          if (value === undefined && column.type !== 'boolean') {
            return (
              <TableCell key={column.name}>
                <div className="italic text-muted-foreground">{t('policyTable.unavailable')}</div>
              </TableCell>
            );
          }

          switch (column.type) {
            case 'string': {
              return (
                <TableCell key={column.name}>
                  {typeof value === 'string' ? <StringCell value={value} /> : null}
                </TableCell>
              );
            }
            case 'boolean': {
              return (
                <TableCell key={column.name}>
                  {typeof value === 'boolean' ? <BooleanCell value={value} /> : null}
                </TableCell>
              );
            }
            case 'status': {
              return (
                <TableCell key={column.name} data-testid="status">
                  <PolicyStatus policy={policy} workingBranch={branch} />
                </TableCell>
              );
            }
            case 'price': {
              return (
                <TableCell key={column.name}>
                  {typeof value === 'number' ? <PriceCell value={value} /> : null}
                </TableCell>
              );
            }
            case 'date': {
              const { format } = column;

              return (
                <TableCell key={column.name}>
                  {typeof value === 'string' ? (
                    <DateCell value={new Date(value)} format={format} />
                  ) : null}
                </TableCell>
              );
            }
            case 'product': {
              return (
                <TableCell key={column.name}>
                  {typeof value === 'string' ? <ProductCell productId={value} /> : null}
                </TableCell>
              );
            }
            case 'tier': {
              return (
                <TableCell key={column.name}>
                  {typeof value === 'string' ? (
                    <TierCell productId={policy.productId} tier={value} />
                  ) : null}
                </TableCell>
              );
            }
            case 'fraudStatus': {
              const status = value as keyof typeof FRAUD_STATUS_TONE | null;
              return (
                <TableCell key={column.name}>
                  {status && FRAUD_STATUS_TONE[status] ? (
                    <Badge variant="light" tone={FRAUD_STATUS_TONE[status]}>
                      {t(`documentReview.fraud.status.${status}`)}
                    </Badge>
                  ) : null}
                </TableCell>
              );
            }
          }
        })}

        <TableCell>
          <Link
            to={`/policies/${policy.id}${branch?.branchId ? `/${branch.branchId}` : ''}${policyLinkSuffix}`}
            onClick={e => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="sm"
              className={cn({ 'hover:bg-gray-300': isMultipleRow && isOpen })}
            >
              {t('policyTable.view')}
              <EyeIcon className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </TableCell>
      </TableRow>
    );
  });
};
